import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import {
  ORDER_STATUS, statusColor, accentColorForType,
} from 'common/src/other/PartnerUtils';
import {
  Box, Typography, Stack, Paper, Chip, Avatar
} from '@mui/material';
import moment from 'moment/min/moment-with-locales';
import { FONT_FAMILY } from '../common/sharedFunctions';

export default function PartnerOrdersInbox() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((s) => s.auth);
  const partnerdata = useSelector((s) => s.partnerdata);
  const partnerorderdata = useSelector((s) => s.partnerorderdata);
  const [filter, setFilter] = useState('ALL');
  const { fetchPartnersByOwner, fetchPartnerOrdersByPartner } = api;

  useEffect(() => {
    if (auth?.profile?.uid) dispatch(fetchPartnersByOwner(auth.profile.uid));
  }, [dispatch, fetchPartnersByOwner, auth?.profile?.uid]);

  const myPartner = (partnerdata.partners || [])[0];

  useEffect(() => {
    if (myPartner?.id) dispatch(fetchPartnerOrdersByPartner(myPartner.id));
  }, [dispatch, fetchPartnerOrdersByPartner, myPartner?.id]);

  const orders = partnerorderdata.orders || [];
  const filtered = useMemo(() => {
    return orders
      .filter((o) => filter === 'ALL' || o.status === filter)
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [orders, filter]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT_FAMILY }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        {t('partner_orders_inbox') || 'Reservas/Pedidos'}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Chip
          label={t('all') || 'Todos'} variant={filter === 'ALL' ? 'filled' : 'outlined'}
          onClick={() => setFilter('ALL')} sx={{ fontWeight: 600 }}
        />
        {Object.values(ORDER_STATUS).map((s) => (
          <Chip
            key={s} label={s} variant={filter === s ? 'filled' : 'outlined'}
            onClick={() => setFilter(s)}
            sx={filter === s
              ? { backgroundColor: statusColor(s), color: '#fff', fontWeight: 600 }
              : { fontWeight: 600 }}
          />
        ))}
      </Stack>

      <Stack spacing={1.25}>
        {filtered.map((o) => (
          <Paper
            key={o.id} variant="outlined"
            sx={{ p: 1.5, borderRadius: 3, cursor: 'pointer' }}
            onClick={() => navigate(`/myorders/${o.id}`)}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar variant="rounded" sx={{ width: 56, height: 56 }}>
                {(o.customerName || '?').slice(0, 1)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontWeight: 600 }} noWrap>{o.customerName || '—'}</Typography>
                  {o.partnerType && (
                    <Chip
                      size="small" label={o.orderType}
                      sx={{
                        backgroundColor: `${accentColorForType(o.partnerType)}1a`,
                        color: accentColorForType(o.partnerType),
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  #{o.reference} · {moment(o.createdAt).format('lll')}
                </Typography>
              </Box>
              <Stack alignItems="flex-end" spacing={0.5}>
                <Chip
                  size="small" label={o.status}
                  sx={{ backgroundColor: `${statusColor(o.status)}1a`, color: statusColor(o.status), fontWeight: 600 }}
                />
                <Typography sx={{ fontWeight: 700 }}>
                  {o.currency} {Number(o.total || 0).toFixed(2)}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        ))}
        {filtered.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {t('no_orders') || 'Sin pedidos aún.'}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
